"use client"

import { useState, useEffect, useRef } from "react"
import { useRouter } from "next/navigation"
import { signOut } from "@/lib/auth-client"
import { LogOut, X } from "lucide-react"
import { cn } from "@/lib/utils"

export function LogoutButton() {
  const router = useRouter()
  const [showConfirm, setShowConfirm] = useState(false)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const dialogRef = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    if (showConfirm) {
      dialog.showModal()
    } else {
      dialog.close()
    }
  }, [showConfirm])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && showConfirm) {
        setShowConfirm(false)
      }
    }
    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [showConfirm])

  const handleLogout = async () => {
    setIsLoggingOut(true)
    await signOut()
    router.push("/admin/login")
  }

  return (
    <>
      <button
        onClick={() => setShowConfirm(true)}
        className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
      >
        Sign Out
      </button>

      {showConfirm && (
        <dialog
          ref={dialogRef}
          className={cn(
            "fixed inset-0 z-50 m-auto",
            "w-full max-w-sm p-0",
            "bg-transparent backdrop:bg-black/60"
          )}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowConfirm(false)
            }
          }}
        >
          <div
            className={cn(
              "bg-bg-card border border-border rounded-xl",
              "p-6 shadow-2xl"
            )}
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <LogOut className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-text">Sign Out</h3>
              </div>
              <button
                onClick={() => setShowConfirm(false)}
                className={cn(
                  "p-1 rounded-lg",
                  "text-text-muted hover:text-text hover:bg-bg-alt",
                  "transition-colors"
                )}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="mb-6">
              <p className="text-text-secondary">
                Are you sure you want to sign out?
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                disabled={isLoggingOut}
                className={cn(
                  "px-4 py-2 rounded-lg",
                  "bg-bg-alt hover:bg-bg",
                  "text-text-secondary hover:text-text",
                  "transition-colors",
                  "disabled:opacity-50"
                )}
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                className={cn(
                  "px-4 py-2 rounded-lg",
                  "bg-primary hover:bg-primary-hover",
                  "text-white font-medium",
                  "transition-colors",
                  "disabled:opacity-50"
                )}
              >
                {isLoggingOut ? "Signing out..." : "Sign Out"}
              </button>
            </div>
          </div>
        </dialog>
      )}
    </>
  )
}
